import { useState, useRef } from "react";
import { Spinner } from "react-bootstrap";
import ModalCloseIcon from "../../../Assets/svg/close-modal.svg";
import ticketLogo from "../../../Assets/svg/ticket_icon.svg";
import { apiCall } from "../../../services/api-service.js";
import { getAttachmentIcon } from "../../common/attachment-icon";

const AddAttachmentModal = ({ ticketDetail, onHide, onSuccess }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    addFiles(selectedFiles);
    e.target.value = null;
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);
    addFiles(droppedFiles);
  };

  const addFiles = (newFiles) => {
    const validFiles = [];
    let sizeError = false;
    let duplicateError = false;

    newFiles.forEach((file) => {
      const isDuplicate = files.some((f) => f.name === file.name && f.size === file.size);

      if (file.size > 5 * 1024 * 1024) {
        sizeError = true;
      } else if (isDuplicate) {
        duplicateError = true;
      } else {
        validFiles.push(file);
      }
    });

    if (sizeError) {
      setError("File size exceeds the maximum limit of 5MB.");
    } else if (duplicateError) {
      setError("Some files are already added");
    } else {
      setError("");
    }

    setFiles((prev) => [...prev, ...validFiles]);
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleSubmit = async () => {
    if (files.length === 0) {
      setError("Please select at least one file");
      return;
    }

    setLoading(true);
    try {
      await Promise.all(
        files.map(async (file) => {
          const base64 = await convertToBase64(file);
          const data = {
            attachment: base64,
            attachment_name: file.name,
          };
          return apiCall("put", `ticket/add_attachment/${ticketDetail.id}`, data);
        }),
      );

      onSuccess();
    } catch (err) {
      console.error("Add Attachment Error:", err);
      setError("An error occurred while adding the attachment");
    } finally {
      setLoading(false);
    }
  };

  const modalStyle = {
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1000,
    },
    modal: {
      backgroundColor: "#ffffff",
      borderRadius: "24px",
      width: "520px",
      padding: "24px",
      boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
    },
    dropZone: {
      border: "1px dashed #212129",
      borderRadius: "12px",
      padding: "40px 20px",
      textAlign: "center",
      cursor: "pointer",
      background: "#F7F8F9",
      transition: "border-color 0.2s ease",
      marginBottom: "24px",
    },
  };

  const scrollbarStyle = `
    .custom-scrollbar::-webkit-scrollbar {
      width: 4px;
    }
    .custom-scrollbar::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 10px;
    }
    .custom-scrollbar::-webkit-scrollbar-thumb {
      background: #888;
      border-radius: 10px;
    }
    .custom-scrollbar::-webkit-scrollbar-thumb:hover {
      background: #555;
    }
  `;

  return (
    <div style={modalStyle.overlay} onClick={onHide}>
      <style>{scrollbarStyle}</style>
      <div style={modalStyle.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 style={{ fontSize: "24px", fontWeight: "700", color: "#212129", margin: 0 }}>Add Attachment</h2>
          <div
            onClick={onHide}
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              border: "1px solid #EBEBEB",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <img src={ModalCloseIcon} alt="close" style={{ width: "14px" }} />
          </div>
        </div>

        {/* Ticket Info Card */}
        <div
          style={{
            background: "#F7F8F9",
            borderRadius: "16px",
            padding: "16px",
            display: "flex",
            gap: "16px",
            alignItems: "flex-start",
            marginBottom: "24px",
          }}
        >
          <div
            style={{
              width: "40px",
              height: "40px",
              background: "#ffff",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <img src={ticketLogo} alt="ticket" style={{ width: "20px" }} />
          </div>
          <div style={{ overflow: "hidden" }}>
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: "#212129",
                marginBottom: "4px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {ticketDetail.code} - {ticketDetail.title}
            </h3>
            <div className="d-flex align-items-center gap-2">
              <span style={{ fontSize: "14px", color: "#8790A1" }}>{ticketDetail.project || "No Project"}</span>
              <span style={{ width: "4px", height: "4px", background: "#D9D9D9", borderRadius: "50%" }}></span>
              <span style={{ fontSize: "14px", color: "#6929E2", fontWeight: "500" }}>{ticketDetail.status}</span>
            </div>
          </div>
        </div>

        {/* Upload Field */}
        <div className="mb-4">
          <label style={{ fontSize: "14px", fontWeight: "600", color: "#212129", marginBottom: "12px", display: "block" }}>Attachment</label>
          <div style={modalStyle.dropZone} onDragOver={(e) => e.preventDefault()} onDrop={handleDrop} onClick={() => fileInputRef.current.click()}>
            <input type="file" ref={fileInputRef} onChange={handleFileChange} multiple style={{ display: "none" }} />
            <div>
              <p style={{ fontSize: "18px", fontWeight: "600", color: "#212129", margin: 0 }}>Click here to select or drag files</p>
              <p style={{ fontSize: "14px", color: "#8790A1", marginTop: "8px" }}>File size must be less than 5MB</p>
            </div>
          </div>

          {files.length > 0 && (
            <div
              className="mb-3 custom-scrollbar"
              style={{
                maxHeight: "240px",
                overflowY: "auto",
                paddingRight: "4px",
                display: "flex",
                flexWrap: "wrap",
                gap: "12px",
              }}
            >
              {files.map((f, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "12px 16px",
                    background: "#F7F8F9",
                    borderRadius: "12px",
                    width: "calc(50% - 6px)",
                    minWidth: "200px",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      flexShrink: 0,
                      marginRight: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img src={getAttachmentIcon(f)} alt="file-icon" style={{ width: "32px", height: "32px" }} />
                  </div>
                  <div style={{ flex: 1, overflow: "hidden" }}>
                    <p
                      style={{
                        fontSize: "13px",
                        fontWeight: "500",
                        color: "#212129",
                        margin: 0,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        fontFamily: "Satoshi",
                      }}
                    >
                      {f.name}
                    </p>
                    <p
                      style={{
                        fontSize: "12px",
                        color: "#8790A1",
                        margin: 0,
                        fontFamily: "Satoshi",
                      }}
                    >
                      {(f.size / (1024 * 1024)).toFixed(2)} MB
                    </p>
                  </div>
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(i);
                    }}
                    role="button"
                    tabIndex="0"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.stopPropagation();
                        removeFile(i);
                      }
                    }}
                    style={{
                      cursor: "pointer",
                      padding: "4px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <img src={ModalCloseIcon} alt="remove" style={{ width: "12px", height: "12px" }} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {error && <span style={{ color: "#F44336", fontSize: "12px", marginTop: "-12px", marginBottom: "12px", display: "block" }}>{error}</span>}
        </div>

        {/* Actions */}
        <div className="d-flex justify-content-end gap-3">
          <button
            onClick={onHide}
            style={{
              padding: "12px 32px",
              borderRadius: "100px",
              border: "1px solid #EBEBEB",
              background: "#FFFFFF",
              fontSize: "16px",
              fontWeight: "700",
              color: "#212129",
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            style={{
              padding: "12px 32px",
              borderRadius: "100px",
              border: "none",
              background: "#6929E2",
              fontSize: "16px",
              fontWeight: "700",
              color: "#FFFFFF",
              cursor: "pointer",
              minWidth: "120px",
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? <Spinner animation="border" size="sm" variant="light" /> : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddAttachmentModal;
